import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, DatePicker } from 'antd';
import { makePostCall, calculateExactAge } from '../utils/helper';
import dayjs from 'dayjs';
import { FieldLabels, getPatientIdLabel } from '../constants/fieldLabels';

const { Option } = Select;
const { TextArea } = Input;

interface EditStudyDetailsModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  /** Patient ID(YH No.) changes are patient-wide (all studies) — only exposed to admins via Manage Studies. */
  allowPatientIdEdit?: boolean;
  studyData: {
    patientName: string;
    patientId: string;
    patientAge: string | number;
    patientDOB?: string;
    patientGender: string;
    studyDescription: string;
    accessionNumber: string;
    referringPhysician: string;
    history: string;
    bodyPart?: string;
    studyUID?: string;
    orderId?: string;
  };
}

const EditStudyDetailsModal: React.FC<EditStudyDetailsModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  allowPatientIdEdit = false,
  studyData
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValues, setInitialValues] = useState<any>({});

  useEffect(() => {
    if (visible && studyData) {
      // Calculate age from DOB if available, otherwise use the provided age
      const calculatedAge = studyData.patientDOB
        ? calculateExactAge(studyData.patientDOB)
        : null;
      const finalAge = calculatedAge || studyData.patientAge || '';

      // Parse DOB from YYYYMMDD format to dayjs object for DatePicker
      const dobDayjs = studyData.patientDOB && dayjs(studyData.patientDOB, 'YYYYMMDD').isValid()
        ? dayjs(studyData.patientDOB, 'YYYYMMDD')
        : null;

      const values = {
        patientName: studyData.patientName || '',
        patientId: studyData.patientId || '',
        patientAge: finalAge,
        patientGender: studyData.patientGender || '',
        studyDescription: studyData.studyDescription || '',
        accessionNumber: studyData.accessionNumber || '',
        referringPhysician: studyData.referringPhysician || '',
        history: studyData.history || '',
        bodyPart: studyData.bodyPart || '',
        patientDOB: dobDayjs
      };

      // Store initial values including DOB for comparison
      setInitialValues({
        ...values,
      });
      form.setFieldsValue(values);

      setHasChanges(false);
    }
  }, [visible, studyData, form]);

  const checkForChanges = () => {
    const currentValues = form.getFieldsValue();
    const isChanged = Object.keys(initialValues).some(key => {
      const initialValue = initialValues[key];
      const currentValue = currentValues[key];

      // Special handling for dayjs objects (DOB field)
      if (key === 'patientDOB') {
        if (!initialValue && !currentValue) return false;
        if (!initialValue || !currentValue) return true;
        if (dayjs.isDayjs(initialValue) && dayjs.isDayjs(currentValue)) {
          return !initialValue.isSame(currentValue, 'day');
        }
        return true;
      }

      // Regular comparison for other fields
      return initialValue !== currentValue;
    });
    setHasChanges(isChanged);
  };

  const handleDOBChange = (date: any) => {
    if (date && dayjs.isDayjs(date)) {
      const dobFormatted = date.format('YYYYMMDD');
      const calculatedAge = calculateExactAge(dobFormatted);
      form.setFieldValue('patientAge', calculatedAge || '');
    } else {
      form.setFieldValue('patientAge', '');
    }
    // Check for changes after updating age
    setTimeout(checkForChanges, 10);
  };

  const getChangedFields = () => {
    const currentValues = form.getFieldsValue();
    const fieldLabels: { [key: string]: string } = {
      patientName: FieldLabels.PATIENT_NAME,
      patientId: getPatientIdLabel(),
      patientDOB: FieldLabels.PATIENT_DOB,
      patientAge: FieldLabels.PATIENT_AGE,
      patientGender: FieldLabels.PATIENT_SEX,
      studyDescription: FieldLabels.STUDY_DESCRIPTION,
      accessionNumber: FieldLabels.ACCESSION_NUMBER,
      referringPhysician: FieldLabels.REFERRING_PHYSICIAN,
      history: FieldLabels.HISTORY,
      bodyPart: FieldLabels.BODY_PART,
    };

    const changedFieldsData: { [key: string]: { old_value: any; new_value: any } } = {};

    Object.keys(initialValues).forEach(key => {
      const initialValue = initialValues[key];
      const currentValue = currentValues[key];

      // Special handling for dayjs objects (DOB field)
      if (key === 'patientDOB') {
        if (!initialValue && !currentValue) return;

        const oldDOB =
          initialValue && dayjs.isDayjs(initialValue) ? initialValue.format('DD-MM-YYYY') : null;
        const newDOB =
          currentValue && dayjs.isDayjs(currentValue) ? currentValue.format('DD-MM-YYYY') : null;

        if (
          !initialValue ||
          !currentValue ||
          (dayjs.isDayjs(initialValue) &&
            dayjs.isDayjs(currentValue) &&
            !initialValue.isSame(currentValue, 'day'))
        ) {
          changedFieldsData[fieldLabels[key]] = {
            old_value: oldDOB,
            new_value: newDOB,
          };
        }
        return;
      }

      // Regular comparison for other fields
      if (initialValue !== currentValue) {
        changedFieldsData[fieldLabels[key]] = {
          old_value: initialValue || null,
          new_value: currentValue || null,
        };
      }
    });

    return changedFieldsData;
  };

  const submitDetails = async (values: any) => {
    setLoading(true);
    try {
      // Convert DOB moment object to YYYYMMDD format
      const dobFormatted = values.patientDOB ? values.patientDOB.format('YYYYMMDD') : null;

      // Get changed fields with old and new values
      const changedFieldsData = getChangedFields();

      const payload = {
        study_uid: studyData.studyUID,
        order_id: studyData.orderId,
        patient_name: values.patientName,
        patient_id: values.patientId,
        patient_dob: dobFormatted,
        patient_age: values.patientAge,
        patient_gender: values.patientGender,
        study_description: values.studyDescription,
        accession_number: values.accessionNumber,
        referring_physician: values.referringPhysician,
        history: values.history,
        // null (not undefined) so an explicit clear survives JSON serialization
        body_part: values.bodyPart ?? null,
        updated_fields: changedFieldsData,
      };

      const response = await makePostCall('/update-study-details', payload);

      if (response.data.success) {
        message.success('Study details updated successfully');
        form.resetFields();
        setHasChanges(false);
        onSuccess?.();
        onCancel();
      } else {
        message.error(response.data.message || 'Failed to update study details');
      }
    } catch (error: any) {
      // The axios interceptor already toasts server-provided messages
      // (e.g. permission denials) — only add the generic one when there isn't any.
      if (!error?.response?.data?.message) {
        message.error('Failed to update study details. Please try again.');
      }
      console.error('Error updating study details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error('Please fill in all required fields');
      return;
    }

    // A YH No(Patient ID) change applies to ALL of the patient's studies (local DB and
    // DICOM archive are both patient-level) — make the admin acknowledge that.
    if (allowPatientIdEdit && values.patientId !== initialValues.patientId) {
      Modal.confirm({
        title: `Change ${getPatientIdLabel()}?`,
        content: (
          <div>
            <p>
              This will change the {getPatientIdLabel()} from <b>{initialValues.patientId || '-'}</b> to{' '}
              <b>{values.patientId}</b> for <b>ALL studies of this patient</b> — not just this one.
            </p>
            <p>
              The new ID must match the patient's HIS ID. If it doesn't, future orders for this
              patient will create a duplicate patient record.
            </p>
          </div>
        ),
        okText: `Change ${getPatientIdLabel()}`,
        okButtonProps: { danger: true },
        onOk: () => submitDetails(values),
      });
      return;
    }

    await submitDetails(values);
  };

  const handleCancel = () => {
    form.resetFields();
    setHasChanges(false);
    onCancel();
  };

  return (
    <Modal
      title="DICOM Edit"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnClose
    >
      <div
        style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#e6f7ff',
          borderRadius: '4px',
          border: '1px solid #91d5ff',
        }}
      >
        <strong>Note:</strong> Updated patient details will appear in the report
      </div>

      <Form
        form={form}
        layout="vertical"
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <Form.Item
            label={FieldLabels.PATIENT_NAME}
            name="patientName"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter patient name' }]}
          >
            <Input
              placeholder="Enter patient name"
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={getPatientIdLabel()}
            name="patientId"
            style={{ marginBottom: 8 }}
            rules={allowPatientIdEdit ? [{ required: true, message: `Please enter ${getPatientIdLabel()}` }] : []}
          >
            <Input
              placeholder={`Enter ${getPatientIdLabel()}`}
              disabled={!allowPatientIdEdit}
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_DOB}
            name="patientDOB"
            style={{ marginBottom: 8 }}
          >
            <DatePicker
              format="DD-MM-YYYY"
              placeholder="Select dob"
              style={{ width: '100%' }}
              disabledDate={current => current && current > dayjs().endOf('day')}
              onChange={handleDOBChange}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_AGE}
            name="patientAge"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter patient age"
              disabled
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.PATIENT_SEX}
            name="patientGender"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please select patient gender' }]}
          >
            <Select
              placeholder="Select gender"
              onChange={checkForChanges}
            >
              <Option value="M">Male</Option>
              <Option value="F">Female</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label={FieldLabels.STUDY_DESCRIPTION}
            name="studyDescription"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter study description' }]}
          >
            <Input
              placeholder="Enter study description"
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.ACCESSION_NUMBER}
            name="accessionNumber"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter accession number"
              disabled
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.REFERRING_PHYSICIAN}
            name="referringPhysician"
            style={{ marginBottom: 8 }}
            rules={[{ required: true, message: 'Please enter referring physician' }]}
          >
            <Input
              placeholder="Enter referring physician name"
              onChange={checkForChanges}
            />
          </Form.Item>

          <Form.Item
            label={FieldLabels.BODY_PART}
            name="bodyPart"
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder="Enter body part"
              allowClear
              onChange={checkForChanges}
            />
          </Form.Item>
        </div>

        <Form.Item
          label={FieldLabels.HISTORY}
          name="history"
          style={{ marginBottom: 24, marginTop: 4 }}
        >
          <TextArea
            placeholder="Enter patient history"
            rows={4}
            maxLength={500}
            showCount
            onChange={checkForChanges}
          />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '12px' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={!hasChanges}
          >
            Submit
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default EditStudyDetailsModal;
